import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Trophy, ThumbsDown, Ban } from "lucide-react";
import { ReasonChips, WIN_REASONS } from "@/lib/lossReasons";
import { celebrateNow } from "@/components/WinCelebration";

// §12.1 vocabulary: Lost reasons are a pick list; Abandoned = we chose not to pursue (never in the close rate).
export const LOST_REASONS = [
  ["client_lost_project", "Client lost the project"],
  ["over_budget", "Over budget"],
  ["bonding", "Bonding requirements"],
  ["other", "Other"],
];

// Close a pursuit — Won, Lost (with a reason) or Abandoned. Pick the outcome, capture WHY, confirm.
export function OutcomeDialog({ bidId, bidName, open, onOpenChange, onDone, initialMode = null }) {
  const [mode, setMode] = useState(null); // won | lost | abandoned
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");
  const [winner, setWinner] = useState("");
  const [winnerPrice, setWinnerPrice] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setMode(initialMode === "cancelled" ? "lost" : initialMode || null); setNote(""); setCode(initialMode === "cancelled" ? "client_lost_project" : ""); setWinner(""); setWinnerPrice(""); } }, [open, initialMode]);

  const pick = (m) => { setMode(m); setNote(""); setCode(""); };
  const close = () => { setMode(null); setNote(""); setCode(""); onOpenChange(false); };

  const record = async () => {
    if (!mode) return;
    if (mode === "lost" && !code) { toast.error("Pick a loss reason."); return; }
    if (mode === "lost" && code === "other" && !note.trim()) { toast.error("Say what happened when the reason is 'other'."); return; }
    if (mode === "abandoned" && !note.trim()) { toast.error("Say why we chose not to pursue it."); return; }
    setSaving(true);
    try {
      const extra = mode === "lost" ? { reason_code: code, winner: winner.trim() || null, winner_price: winnerPrice.replace(/[$,\s]/g, "") || null } : {};
      const res = await api.post(`/bids/${bidId}/outcome`, { outcome: mode, note: note.trim(), ...extra });
      if (mode === "won") celebrateNow();
      toast.success({ won: "Marked won 🎉", lost: "Recorded loss", abandoned: "Marked abandoned" }[mode]);
      setMode(null); setNote("");
      onOpenChange(false);
      onDone?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record the outcome");
    } finally {
      setSaving(false);
    }
  };

  const options = [
    { key: "won", label: "Won", icon: Trophy, cls: "border-emerald-300 text-emerald-700 data-[on=true]:bg-emerald-500 data-[on=true]:text-white data-[on=true]:border-emerald-500" },
    { key: "lost", label: "Lost", icon: ThumbsDown, cls: "border-rose-200 text-rose-700 data-[on=true]:bg-rose-600 data-[on=true]:text-white data-[on=true]:border-rose-600" },
    { key: "abandoned", label: "Abandoned", icon: Ban, cls: "border-slate-300 text-slate-600 data-[on=true]:bg-slate-600 data-[on=true]:text-white data-[on=true]:border-slate-600" },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent data-testid="outcome-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Record outcome — {bidName}</DialogTitle>
          <DialogDescription>
            Won and Lost feed the close rate. A project the client killed after we bid is Lost (client lost the project). Abandoned means we chose not to pursue it — never a loss.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          {options.map((s) => (
            <button
              key={s.key}
              data-testid={`outcome-${s.key}-btn`}
              data-on={mode === s.key}
              onClick={() => pick(s.key)}
              className={`flex items-center justify-center gap-1.5 h-10 rounded-md border text-sm font-medium transition-colors ${s.cls}`}
            >
              <s.icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </div>

        {mode === "lost" && (
          <div className="space-y-2 pt-1">
            <Label className="text-xs">Loss reason</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="outcome-loss-reason-presets">
              {LOST_REASONS.map(([k, label]) => (
                <button key={k} type="button" onClick={() => setCode(k)} data-testid={`outcome-loss-reason-${k}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${code === k ? "bg-rose-600 text-white border-rose-600" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  {label}
                </button>
              ))}
            </div>
            <Textarea data-testid="outcome-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={code === "other" ? "What happened? (required for 'other')" : "Anything worth remembering (optional)"} />
            <div className="grid grid-cols-2 gap-2 pt-1" data-testid="outcome-winner-fields">
              <div>
                <Label className="text-xs">Who won? <span className="text-slate-400">(optional)</span></Label>
                <Input value={winner} onChange={(e) => setWinner(e.target.value)} placeholder="Company that won" className="h-9 mt-1" data-testid="outcome-winner" />
              </div>
              <div>
                <Label className="text-xs">Winning price <span className="text-slate-400">(optional)</span></Label>
                <Input value={winnerPrice} onChange={(e) => setWinnerPrice(e.target.value)} inputMode="decimal" placeholder="$ if you know it" className="h-9 mt-1 font-mono" data-testid="outcome-winner-price" />
              </div>
            </div>
          </div>
        )}

        {mode === "won" && (
          <div className="space-y-2 pt-1">
            <Label className="text-xs">Win reason <span className="text-slate-400">— pick a standard reason (optional) or write your own</span></Label>
            <ReasonChips reasons={WIN_REASONS} value={note} onPick={setNote} testidPrefix="outcome-win-reason" tone="emerald" />
            <Textarea data-testid="outcome-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why did we win? e.g. Best price + existing relationship." />
          </div>
        )}

        {mode === "abandoned" && (
          <div className="space-y-2 pt-1">
            <Label className="text-xs">Why we chose not to pursue it</Label>
            <Textarea data-testid="outcome-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Out of territory; no capacity this quarter; scope not ours." />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} disabled={saving} data-testid="outcome-cancel-btn">Cancel</Button>
          <Button
            data-testid="outcome-confirm-btn"
            disabled={saving || !mode}
            onClick={record}
            className="bg-[#003E87] hover:bg-[#00306a] text-white font-semibold"
          >
            {mode ? `Confirm ${mode}` : "Choose an outcome"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
